import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import * as Fire from '@angular/fire/database';
import { CommunityTenancyData } from 'src/lib/fliko/models/agreements/communityTenancy.data';
import { TenancyAgreementData } from 'src/lib/fliko/models/agreements/tenancyAgr.data';
import { CompanyData, CompanyReportFeature } from 'src/lib/fliko/models/company/company.data';
import { CompanyEmployeeData, EmployeeData } from 'src/lib/fliko/models/company/employee.data';
import { EmailDictionary, PhoneDictionary } from 'src/lib/fliko/models/dictionary.data';
import { CompanyEmailBrandingSettings, EstateEmailBrandingSettings } from 'src/lib/fliko/models/email/email-branding.data';
import { EstateDetailsData } from 'src/lib/fliko/models/estate/estate-detail.data';
import { EstateFeatures } from 'src/lib/fliko/models/estate/estate-features';
import { EstateSettingsData } from 'src/lib/fliko/models/estate/estate-settings.data';
import { EstateData, EstateDbSettingsData } from 'src/lib/fliko/models/estate/estate.data';
import { EstateSubcontractorData } from 'src/lib/fliko/models/estate/subcontractor.data';
import { EventLog } from 'src/lib/fliko/models/eventLog.data';
import { ChangeUserLicenseCtx, GetAccessProfileForUserCtx } from 'src/lib/fliko/models/http/blueboltAccess.data';
import { EmailAllCtx } from 'src/lib/fliko/models/http/emailAll';
import { AdminCtx, EmployeeCtx, EmpRoleCtx, EstateCtx, UnitCtx } from 'src/lib/fliko/models/http/request.data';
import { RunWelesByHandCtx } from 'src/lib/fliko/models/http/runWelesByHand.data';
import {
  AddCompanyEmpCtx,
  ChangeResponse,
  CreateEstateCtx,
  CreateUnitCtx,
  EstateCollection,
  EstateCollectionCtx,
  EstateObject,
  EstateObjectCtx,
  ResidentChangeCodeCtx,
  ResidentChangeUserCtx,
  UpdateCompanyEstatesCtx,
  UpdateEstateObjCtx,
  UpdateUnitLegalOwnershipCtx,
  WholeCollection,
  WholeCollectionCtx
} from 'src/lib/fliko/models/http/superAdminRequest.data';
import { SauterActionReq } from 'src/lib/fliko/models/integrations/sauter.data';
import { WebconMeterSyncResult } from 'src/lib/fliko/models/integrations/webcon.data';
import { WelesParsed } from 'src/lib/fliko/models/integrations/weles/welesParsed.types';
import { OfferFeature } from 'src/lib/fliko/models/offer.data';
import { ResidentData } from 'src/lib/fliko/models/person/resident.data';
import { MeterData } from 'src/lib/fliko/models/reckonings/meters.data';
import { UnitBalanceData } from 'src/lib/fliko/models/reckonings/unit.reck.data';
import { BalanceUpdateUploadLog } from 'src/lib/fliko/models/reckonings/uploadLog.data';
import { ApartmentData } from 'src/lib/fliko/models/units/apartment.data';
import { ParkingData } from 'src/lib/fliko/models/units/parking.data';
import { StorageData } from 'src/lib/fliko/models/units/storage.data';
import { RentalUnitWithType } from 'src/lib/fliko/models/units/unit.data';
import { DbUpdateAction } from 'src/lib/fliko/models/updateActions.data';
import { CRMAdminAccessData, UserAccessData, UserEstateAccessData } from 'src/lib/fliko/models/users/access.data';
import { CreatedUser } from 'src/lib/fliko/models/users/createdUser.data';
import { ShareData } from 'src/lib/fliko/models/users/share.data';
import { EstateAdminData, ServiceUserData, UserType } from 'src/lib/fliko/models/users/user.data';
import { Collection } from 'src/lib/fliko/types/database.type';
import { Nullish } from 'src/lib/fliko/types/typeish.type';
import { Update } from 'src/lib/fliko/types/update.type';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { DatabaseService } from 'src/app/shared/services/database.service';
import { FireDbService } from 'src/app/shared/services/fireDb.service';
import { FunctionsApiService, Result } from 'src/app/shared/services/functions-api.service';
import { EstateService } from 'src/app/view/estate-view/services/estate.service';

const WEBCON_SYNC_TIMEOUT_MS = 550_000;

@Injectable({ providedIn: 'root' })
export class AdminService {
  constructor(
    private dbSrv: DatabaseService,
    private fas: FunctionsApiService,
    private errorService: ErrorHandlingService,
    private estateService: EstateService
  ) {}

  // #region Helpers
  private onReject = (reason: any) => {
    if (reason.code === 'functions/permission-denied') {
      this.errorService.showGlobalError(this.errorService.getErrorText('permDenied') ?? reason.message);
    }
  };

  private callFunc<T, R>(name: string, data: R): Result<T> {
    return this.fas.callFunction<T>(name, data, this.onReject);
  }

  private getEstateObj<T>(estateId: string, name: EstateObject): Result<T | null> {
    return this.callFunc<T, EstateObjectCtx>('getEstateObject', { estateId, name });
  }

  private getEstateCol<T>(estateId: string, name: EstateCollection): Result<T[]> {
    return this.callFunc<T[], EstateCollectionCtx>('getEstateCollection', { estateId, name });
  }

  private getWholeCol<T>(name: WholeCollection): Result<T[]> {
    return this.callFunc<T[], WholeCollectionCtx>('getWholeCollection', { name, asMap: false });
  }

  private getWholeColObj<T>(name: WholeCollection): Result<Collection<Collection<T>>> {
    return this.callFunc<Collection<Collection<T>>, WholeCollectionCtx>('getWholeCollection', { name, asMap: true });
  }

  private updateEstateObj<T>(estateId: string, name: EstateObject, data: T): Result<boolean> {
    return this.callFunc<boolean, UpdateEstateObjCtx>('updateEstateObject', { estateId, name, data });
  }

  // #endregion Helpers

  // #region database
  public sendDbUpdateAction(action: DbUpdateAction): Result<void> {
    return this.callFunc<void, DbUpdateAction>('databaseUpdateData', action);
  }

  public sendEmailToAll(subject: string, message: string): Result<boolean> {
    return this.callFunc<boolean, EmailAllCtx>('emailAllUsers', { subject, message });
  }
  // #endregion database

  // #region Estate
  /**
   * @path /estate/
   * @key id
   */
  private get estate() {
    return this.dbSrv.commonFactory.getCollection<EstateData>('estate');
  }

  public getEstates$(): Observable<EstateData[]> {
    return this.estate.getList();
  }

  public getSuperadminEventLogs(estateId: string): Result<EventLog[]> {
    return this.fas.callFunctionOrThrow<EstateCollectionCtx, EventLog[]>('getEstateCollection', {
      estateId,
      name: 'superadminEventLog'
    });
  }

  public createEstate(data: CreateEstateCtx): Result<boolean> {
    return this.callFunc<boolean, CreateEstateCtx>('createEstate', data);
  }

  public updateEstate(estateData: Update<EstateData>): Promise<void> {
    return this.estate.updateElement(estateData.id, estateData);
  }

  public removeEstateData(estateId: string): Result<void> {
    return this.callFunc<void, EstateCtx>('estateDataRemove', { estateId });
  }

  public sauterAction(req: SauterActionReq): Result<void> {
    return this.callFunc<void, SauterActionReq>('sauterAction', req);
  }

  public syncWebconMeters(estateId: string): Promise<WebconMeterSyncResult> {
    return this.fas.callFunctionOrThrow<EstateCtx, WebconMeterSyncResult>(
      'syncWebconMeters',
      { estateId },
      { timeout: WEBCON_SYNC_TIMEOUT_MS }
    );
  }

  public getEstateFeatures(estateId: string): Result<EstateFeatures | Nullish> {
    return this.getEstateObj(estateId, 'estateFeatures');
  }

  public updateEstateFeatures(estateId: string, features: EstateFeatures): Result<boolean> {
    return this.updateEstateObj(estateId, 'estateFeatures', features);
  }

  public getEstateSettings(estateId: string): Result<EstateSettingsData | null> {
    return this.fas.callFunctionOrThrow<EstateObjectCtx, EstateSettingsData | null>('getEstateObject', {
      estateId,
      name: 'estateSettings'
    });
  }

  public updateEstateSettings(estateId: string, settings: EstateSettingsData): Result<boolean> {
    return this.updateEstateObj(estateId, 'estateSettings', settings);
  }

  public updateEstateEmailBranding(estateId: string, emailBranding: EstateEmailBrandingSettings): Result<boolean> {
    return this.fas.callFunctionOrThrow<{ estateId: string; emailBranding: EstateEmailBrandingSettings }, boolean>(
      'updateEstateEmailBranding',
      {
        estateId,
        emailBranding
      }
    );
  }

  public getEstateDetails(estateId: string): Result<EstateDetailsData | Nullish> {
    return this.getEstateObj(estateId, 'estateDetails');
  }

  /**
   * @param {string} estateId
   * @path /estateDbSettings/$estateId
   */
  public getEstateDbSettings(estateId: string): Promise<EstateDbSettingsData | Nullish> {
    return this.estateService.getEstateDbSettingsFirst(estateId);
  }

  public updateEstateDbSettings(data: EstateDbSettingsData): Promise<void> {
    return this.estateService.updateEstateDbSettings(data);
  }

  // #endregion Estate

  // #region Dictionary
  /**
   * @path /dictionary/email/
   * @key email sanitized
   */
  private get emailDictionary() {
    return this.dbSrv.commonFactory.getCollection<EmailDictionary>('dictionary/email', { keyField: 'email' });
  }

  public getEmailDictionary$(): Observable<EmailDictionary[]> {
    return this.emailDictionary.getList();
  }

  /**
   * @path /dictionary/phone/
   * @key phone sanitized
   */
  private get phoneDictionary() {
    return this.dbSrv.commonFactory.getCollection<PhoneDictionary>('dictionary/phone', { keyField: 'phone' });
  }

  public getPhoneDictionary$(): Observable<PhoneDictionary[]> {
    return this.phoneDictionary.getList();
  }
  // #endregion Dictionary

  // #region Create User
  /**
   * @path /createUser/
   * @key id
   */
  private get createUser() {
    return this.dbSrv.commonFactory.getCollection<CreatedUser>('createUser');
  }

  public setCreateUser(userData: CreatedUser): Promise<void> {
    return this.createUser.setElement(userData.id, userData);
  }

  public getCreatedUsers$(): Observable<CreatedUser[]> {
    return this.createUser.getList();
  }
  // #endregion Create User

  // #region Access
  /**
   * @path /estateUserAccess/
   * @key userId
   */
  private get estateUserAccess() {
    return this.dbSrv.commonFactory.getCollection<UserEstateAccessData>('estateUserAccess');
  }

  public getEstateUserAccessMap$(): Observable<Map<string, UserEstateAccessData>> {
    return this.estateUserAccess.getMap();
  }

  /**
   * @path /crmAdminAccess/
   * @key userId
   */
  private get crmAdminAccess() {
    return this.dbSrv.commonFactory.getCollectionReadOnly<CRMAdminAccessData>('crmAdminAccess');
  }

  public getCRMAdminAccessMap$(): Observable<Map<string, CRMAdminAccessData>> {
    return this.crmAdminAccess.getMap();
  }

  public getUserCRMAdminAccess$(userId: string): Observable<CRMAdminAccessData | Nullish> {
    return this.crmAdminAccess.getElement(userId);
  }
  // #endregion Access

  // #region Estate Admin
  public addEstateAdmin(userId: string, estateId: string): Result<boolean> {
    return this.callFunc('addEstateAdmin', { userId, estateId });
  }

  public getEstateAdmins(): Result<EstateAdminData[]> {
    return this.getWholeCol<EstateAdminData>('estateAdmin');
  }
  // #endregion Estate Admin

  // #region Company
  private get company() {
    return this.dbSrv.commonFactory.getCollection<CompanyData>('company');
  }

  public getCompanies$(): Observable<CompanyData[]> {
    return this.company.getList();
  }

  public updateCompanyEmailBranding(companyId: string, emailBranding: CompanyEmailBrandingSettings): Result<boolean> {
    return this.fas.callFunctionOrThrow<{ companyId: string; emailBranding: CompanyEmailBrandingSettings }, boolean>(
      'updateCompanyEmailBranding',
      { companyId, emailBranding }
    );
  }

  public sendEmailTemplateTest(recipientEmail: string, companyId: string, estateId?: string): Result<boolean> {
    return this.fas.callFunctionOrThrow<{ recipientEmail: string; companyId: string; estateId?: string }, boolean>(
      'sendEmailTemplateTest',
      {
        recipientEmail,
        companyId,
        ...(estateId ? { estateId } : {})
      }
    );
  }

  public setCompanyAcApp(companyId: string, acApp: boolean): Promise<void> {
    return this.company.updateElement(companyId, { acApp });
  }

  public createCompany(company: CompanyData): Result<boolean> {
    return this.callFunc<boolean, CompanyData>('createCompany', company);
  }

  public addEstateToCompany(companyId: string, estateId: string): Result<boolean> {
    return this.callFunc<boolean, UpdateCompanyEstatesCtx>('updateCompanyEstates', { companyId, estateId, mode: 'add' });
  }

  public removeEstateFromCompany(companyId: string, estateId: string): Result<boolean> {
    return this.callFunc<boolean, UpdateCompanyEstatesCtx>('updateCompanyEstates', { companyId, estateId, mode: 'remove' });
  }

  public assignCompanyDefectTypes(companyId: string, estateId: string): Result<boolean> {
    return this.callFunc<boolean, AdminCtx>('assignCompanyDefectTypes', { companyId, estateId });
  }

  public getCompanyOfferFeature(companyId: string): Result<OfferFeature | null> {
    return this.fas.callFunctionOrThrow<{ companyId: string }, OfferFeature | null>('getCompanyOfferFeature', { companyId });
  }

  public updateCompanyOfferFeature(companyId: string, feature: OfferFeature): Result<boolean> {
    return this.fas.callFunctionOrThrow<{ companyId: string; feature: OfferFeature }, boolean>('updateCompanyOfferFeature', {
      companyId,
      feature
    });
  }

  public getCompanyReportFeature(companyId: string): Result<CompanyReportFeature | null> {
    return this.fas.callFunctionOrThrow<{ companyId: string }, CompanyReportFeature | null>('getCompanyReportFeature', {
      companyId
    });
  }

  public updateCompanyReportFeature(companyId: string, feature: CompanyReportFeature): Result<boolean> {
    return this.fas.callFunctionOrThrow<{ companyId: string; feature: CompanyReportFeature }, boolean>(
      'updateCompanyReportFeature',
      {
        companyId,
        feature
      }
    );
  }
  // #endregion Company

  // #region Units
  public getApartments(estateId: string): Result<ApartmentData[]> {
    return this.getEstateCol<ApartmentData>(estateId, 'apartment');
  }

  public getParkingSpots(estateId: string): Result<ParkingData[]> {
    return this.getEstateCol<ParkingData>(estateId, 'parkingSpot');
  }

  public getStorages(estateId: string): Result<StorageData[]> {
    return this.getEstateCol<StorageData>(estateId, 'storage');
  }

  public createUnit(data: CreateUnitCtx): Result<boolean> {
    return this.callFunc<boolean, CreateUnitCtx>('createUnit', data);
  }

  public deleteUnit(unit: RentalUnitWithType): Result<boolean> {
    return this.callFunc<boolean, UnitCtx>('deleteUnit', { id: unit.id, type: unit.unitType, estateId: unit.estateId });
  }

  public updateUnitLegalOwnership(unit: RentalUnitWithType, legalOwnership: boolean): Result<boolean> {
    const data: UpdateUnitLegalOwnershipCtx = {
      id: unit.id,
      estateId: unit.estateId,
      type: unit.unitType,
      legalOwnership
    };
    return this.callFunc<boolean, UpdateUnitLegalOwnershipCtx>('updateUnitLegalOwnership', data);
  }
  // #endregion Units

  // #region Employee
  public getCompanyEmployees(): Result<Collection<Collection<CompanyEmployeeData>>> {
    return this.getWholeColObj('companyEmployee');
  }

  private get employee() {
    return this.dbSrv.commonFactory.getCollection<EmployeeData>('employee');
  }

  public getEmployees$(): Observable<EmployeeData[]> {
    return this.employee.getList();
  }

  public addEmployeeToCompany(data: AddCompanyEmpCtx): Result<boolean> {
    return this.callFunc<boolean, AddCompanyEmpCtx>('addEmployeeToCompany', data);
  }

  public removeEmployeeFromCompany(data: EmployeeCtx): Result<boolean> {
    return this.callFunc<boolean, EmployeeCtx>('removeEmployeeFromCompany', data);
  }

  public changeEmployeeRole(data: EmpRoleCtx): Result<boolean> {
    return this.callFunc<boolean, EmpRoleCtx>('setEmployeeRole', data);
  }
  // #endregion Employee

  // #region Shares Agreements
  public getShares(estateId: string): Result<ShareData[]> {
    return this.getEstateCol(estateId, 'unitShare');
  }

  public getAgreements(estateId: string): Result<TenancyAgreementData[]> {
    return this.getEstateCol(estateId, 'tenancyAgreement');
  }

  public getTenancies(estateId: string): Result<CommunityTenancyData[]> {
    return this.getEstateCol(estateId, 'tenancyInCommunity');
  }
  // #endregion Shares Agreements

  // #region Residents
  public getAllResidentsColObj(): Result<Collection<Collection<ResidentData>>> {
    return this.getWholeColObj<ResidentData>('resident');
  }

  public getResidents(estateId: string): Result<ResidentData[]> {
    return this.getEstateCol<ResidentData>(estateId, 'resident');
  }

  public updateResidentOwnerCode(
    estateId: string,
    residents: { residentId: string; ownerCode: string }[]
  ): Result<ChangeResponse> {
    return this.callFunc<ChangeResponse, ResidentChangeCodeCtx>('updateResidentOwnerCode', { estateId, residents });
  }

  public addUserToResident(resident: Update<ResidentData>, userId: string): Result<boolean> {
    return this.callFunc<boolean, ResidentChangeUserCtx>('changeResidentUser', {
      userId,
      residentId: resident.id,
      estateId: resident.estateId
    });
  }

  public removeUserFromResident(resident: Update<ResidentData>): Result<boolean> {
    return this.callFunc<boolean, ResidentChangeUserCtx>('changeResidentUser', {
      userId: null,
      residentId: resident.id,
      estateId: resident.estateId
    });
  }
  // #endregion Residents

  /** @return Obj structure: Collection<estateId, Collection<subcontractorId, EstateSubcontractorData>> */
  public getAllEstateSubcontractorsColObj(): Result<Collection<Collection<EstateSubcontractorData>>> {
    return this.getWholeColObj<EstateSubcontractorData>('estateSubcontractor');
  }

  public getEstateUsersColObj(): Result<Collection<Collection<UserAccessData>>> {
    return this.getWholeColObj<UserAccessData>('estateUser');
  }

  // #region Service user
  /**
   * @path /service/user/
   * @key userId
   */
  private get serviceUser() {
    return this.dbSrv.commonFactory.getCollection<ServiceUserData>('service/user');
  }

  public async setDeleteServiceUser(id: string): Promise<void> {
    return this.serviceUser.setElementChild(id, 'userType', UserType.DELETED);
  }
  // #endregion Service user

  // #region Reckonings & meters
  public getUnitBalance(estateId: string): Promise<UnitBalanceData[]> {
    return this.getEstateCol(estateId, 'unitBalance');
  }

  public getUnitMeters(estateId: string): Promise<MeterData[]> {
    return this.getEstateCol(estateId, 'unitMeter');
  }

  public setBalanceUpdateUploadLog(log: BalanceUpdateUploadLog): Result<boolean> {
    return this.updateEstateObj(log.estateId, 'uploadLog/balanceUpdate', log);
  }
  // #endregion Reckonings & meters

  // #region Integrations

  // #region Bluebolt
  public getBBAccessProfiles(): Result<string> {
    return this.callFunc<string, any>('getBlueboltAccessProfiles', {});
  }

   public async bbChangeUserLicense(data: ChangeUserLicenseCtx): Result<boolean> {
    return this.callFunc<boolean, ChangeUserLicenseCtx>('bbChangeUserLicense', data);
  }

  public async getAccessProfileForUser(data: GetAccessProfileForUserCtx): Result<string> {
    return this.callFunc<string, GetAccessProfileForUserCtx>('getAccessProfileForUser', data);
  }

  // #endregion Bluebolt

  // #region Weles

  public getWelesEstates(): Result<WelesParsed.Estate[]> {
    return this.callFunc<WelesParsed.Estate[], any>('getWelesEstates', {});
  }

  public getWelesUnitsLandlords(estateId: string): Result<string> {
    return this.callFunc<string, string>('getWelesUnitsLandlords', estateId);
  }

  public runWelesLandlordParser(estateId: string): Result<boolean> {
    return this.callFunc<boolean, EstateCtx>('runWelesLandlordParserForEstate', { estateId });
  }

  public runWelesBalanceParser(estateId: string): Result<boolean> {
    return this.callFunc<boolean, EstateCtx>('runWelesBalanceParserForEstate', { estateId });
  }

  public async runWelesByHand(type: RunWelesByHandCtx): Result<void> {
    return this.callFunc<void, RunWelesByHandCtx>('runWelesByHand', type);
  }
  // #endregion Weles

  // #endregion Integrations

  public async getAllShards(): Promise<Set<string>> {
    const shardNames = await this.dbSrv.commonFactory.getCollection<string>('contextDb').onceList();
    return new Set([FireDbService.commonDbName, ...shardNames]);
  }

  public async updateDbObject(data: any, shardName: string, path: string): Promise<void> {
    if (!shardName || !path) throw new Error('Shard name and path must be provided');
    if (!data || !Object.keys(data).length) throw new Error('Data must be a non-empty object');

    const dbRef = FireDbService.getDbRef(shardName);
    await Fire.update(Fire.ref(dbRef, path), data);
  }


}
